// Death → respawn lifecycle. Owns the death overlay timing and the fresh-run
// state reset: world reseed, persisted tier/skill reapplication, DNA-orb drop
// at the death spot, and reset hooks for cross-frame module state (damage
// edge-detection, ripples, narrator queue) so nothing bleeds between lives.

import type { Entity } from './components'
import type { GameWorld, WorldCtx } from './world'
import type { Camera } from '../engine/camera'
import { seedDemo } from './world'
import { applyTier, currentTier } from './evolution'
import { applyPlayerRadius } from './growth'
import { recomputeMods } from './mods'
import { spawnDnaOrb } from './systems/dna-orb'
import { clearRipples } from './systems/particles'
import { resetDamageState } from './systems/damage'
import { resetGraze } from './graze'
import { noteRespawn } from './systems/npc'
import { resetMotherRest } from './systems/mother'
import { attunedCheckpointPos } from './systems/checkpoint'
import { clearNarratorOnRespawn } from '../engine/narrator'

export interface Lifecycle {
  showDeath(): void
  doRespawn(): void
}

export function createLifecycle(deps: {
  world: GameWorld
  camera: Camera
  playerRef: { current: Entity }
  deathOverlay: HTMLElement | null
  /** World seed + generated layout — respawn reseeds the same world (B2). */
  worldCtx?: WorldCtx
  /** Reset frame timing and reschedule the loop — the loop stops on death. */
  onRespawned: () => void
}): Lifecycle {
  let autoRestartTimer: ReturnType<typeof setTimeout> | null = null

  function doRespawn(): void {
    if (autoRestartTimer !== null) {
      clearTimeout(autoRestartTimer)
      autoRestartTimer = null
    }
    deps.deathOverlay?.classList.remove('visible')
    clearNarratorOnRespawn()
    resetDamageState()
    // Fresh life, fresh buffet — the reseeded world hasn't been grazed.
    resetGraze()
    resetMotherRest()
    // Wipe in-flight ripples so a death blast doesn't bleed into the next life.
    clearRipples()

    const prev = deps.playerRef.current
    const deathPos = { ...prev.position }
    const dnaLost = prev.dna ?? 0
    // Eating-growth travels with the orb (VS study): retrieval gives your
    // body back, so death doesn't compound the size loss.
    const sizeLost = prev.sizeFromEating ?? 0
    // Tier survives death (mirrors how skills survive — both are persisted).
    const carriedTier = currentTier(prev)
    // Gear survives death (epic 9ro decision: equipment is never in the death
    // orb): the whole collection plus what's worn.
    const carriedInventory = prev.inventory
    const carriedEquipped = prev.equipped
    // Key items are traversal progress — they survive death like gear (B4).
    const carriedKeys = prev.keys

    deps.world.clear()
    const fresh = seedDemo(deps.world, deps.worldCtx)
    deps.playerRef.current = fresh

    if (carriedTier > 1) {
      applyTier(fresh, carriedTier, { animate: false })
      applyPlayerRadius(fresh)
    }
    if (carriedInventory) fresh.inventory = carriedInventory
    if (carriedEquipped) fresh.equipped = { ...carriedEquipped }
    if (carriedKeys) fresh.keys = [...carriedKeys]
    // Fold mods on the fresh player now (rather than waiting a frame) so the
    // base max-HP baseline is cached before anything else touches health.
    recomputeMods(fresh)

    // Spawn DNA orb at death location if there was anything to lose. Stays
    // exactly the same in both branches below — checkpoints cut travel,
    // they never soften the retrieval tension.
    if (dnaLost > 0 || sizeLost > 0) {
      spawnDnaOrb(deps.world, deathPos.x, deathPos.y, dnaLost, true, sizeLost)
    }

    // Respawn at the last-attuned daughter cell if one resolves; otherwise
    // the origin hub, same as before checkpoints existed. A stale persisted
    // id (world rerolled) falls back to origin rather than crashing.
    const cp = attunedCheckpointPos(deps.world)
    if (cp) {
      fresh.position.x = cp.x
      fresh.position.y = cp.y
      deps.camera.x = cp.x
      deps.camera.y = cp.y
    } else {
      deps.camera.x = 0
      deps.camera.y = 0
      // Her next greeting acknowledges the death ("Look at the state of
      // you.") — only when respawning at her, i.e. the origin branch.
      noteRespawn()
    }
    deps.camera.resetShake()
    deps.onRespawned()
  }

  function showDeath(): void {
    deps.deathOverlay?.classList.add('visible')
    autoRestartTimer = setTimeout(doRespawn, 2000)
  }

  return { showDeath, doRespawn }
}
